import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import apiClient from './client';
import { API } from './endpoints';

/**
 * The top bar search (GlobalSearch) for items and recipes.
 *
 * Residents are searched in the browser from the list the site already holds,
 * so they answer on every keystroke. Items and recipes are thousands of rows
 * that the browser does not hold, so each is its own small server query
 * (GET api/item|recipe/GlobalSearch, capped), debounced, and neither waits on
 * the other or on the resident results.
 */

/** Server-side minimum (GlobalSearchDAL.MinQueryLength): one letter matches most items. */
export const GLOBAL_SEARCH_MIN_LENGTH = 2;
export const GLOBAL_SEARCH_DEBOUNCE_MS = 250;
export const MAX_ITEMS = 6;
export const MAX_RECIPES = 6;

/**
 * GlobalSearchDAL.ItemHit: an item (ItemType 1, what the Items list and Item
 * Add/Mod show). menuDisplayName is this facility's own when it overrides the item's.
 */
export interface ItemSearchHit {
  pKey_Item: number;
  itm_Descr: string;
  menuDisplayName: string | null;
  fKey_RC_Recipe: number | null;
}

/** GlobalSearchDAL.RecipeHit. */
export interface RecipeSearchHit {
  pKey_RC_Recipe: number;
  name: string;
  menuName: string | null;
  recipeNumber: string | null;
  itemTypeName: string | null;
  consistencyName: string | null;
  isPsi: boolean;
}

export const globalSearchKeys = {
  items: (facilityId: number, q: string) => ['globalSearch', 'items', facilityId, q] as const,
  recipes: (facilityId: number, q: string) => ['globalSearch', 'recipes', facilityId, q] as const,
};

/** `value` once it has stopped changing for `ms`. */
export function useDebouncedValue<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setSettled(value), ms);
    return () => clearTimeout(id);
  }, [value, ms]);
  return settled;
}

/** The query as the server will see it, or '' when it is too short to send. */
export function searchableQuery(query: string): string {
  const q = query.trim();
  return q.length >= GLOBAL_SEARCH_MIN_LENGTH ? q : '';
}

export function useItemSearch(facilityId: number, query: string, enabled: boolean) {
  const q = searchableQuery(query);
  return useQuery({
    queryKey: globalSearchKeys.items(facilityId, q),
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<ItemSearchHit[]>(API.itemGlobalSearch(facilityId, q, MAX_ITEMS), { signal });
      return data ?? [];
    },
    enabled: enabled && facilityId > 0 && q !== '',
    staleTime: 60_000,
    retry: false,
  });
}

export function useRecipeSearch(facilityId: number, query: string, enabled: boolean) {
  const q = searchableQuery(query);
  return useQuery({
    queryKey: globalSearchKeys.recipes(facilityId, q),
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<RecipeSearchHit[]>(API.recipeGlobalSearch(facilityId, q, MAX_RECIPES), { signal });
      return data ?? [];
    },
    enabled: enabled && facilityId > 0 && q !== '',
    staleTime: 60_000,
    retry: false,
  });
}

/** "CHICKEN SALAD (Puree)": the Recipes list's full name, so same-named versions can be told apart. */
export function recipeDisplayName(r: RecipeSearchHit): string {
  const suffix = [r.itemTypeName, r.consistencyName].filter(Boolean).join(' - ');
  return suffix ? `${r.name} (${suffix})` : r.name;
}
